/**
 * Service Port Configuration - Single Source of Truth
 *
 * Ports are defined in /config/ports.json (the canonical source).
 * This file reads from the JSON and provides TypeScript types and helpers.
 *
 * Ports can be overridden via environment variables in .env.local (gitignored).
 * If not set, defaults from ports.json are used.
 *
 * @example
 * ```typescript
 * import { getPort, getUrl, PORTS, URLS } from '@ops-matrix/shared-types';
 *
 * // Dynamic - reads from env, falls back to default (recommended)
 * const port = getPort('API_GATEWAY'); // reads API_GATEWAY_PORT env var
 * const apiUrl = getUrl('API_GATEWAY'); // reads API_GATEWAY_URL env var
 *
 * // Static - use defaults directly (for Zod schema defaults)
 * NEXT_PUBLIC_API_URL: z.string().url().default(URLS.API_GATEWAY),
 * ```
 *
 * Environment variables (in .env.local - gitignored):
 * ```
 * API_GATEWAY_PORT=3000
 * API_GATEWAY_URL=http://localhost:3000
 * ```
 *
 * @module config/ports
 */

import portsJson from '../../config/ports.json';

// Type for port entry in JSON
type PortEntry = { port: number; description: string };

// Extract all services from the JSON structure (including metrics)
const allServices = {
  ...portsJson.services.http,
  ...portsJson.services.portals,
  ...portsJson.services.grpc_ai_ml,
  ...portsJson.services.grpc_business,
  ...portsJson.metrics,
} as const satisfies Record<string, PortEntry>;

/**
 * Default port assignments (loaded from ports.json)
 * Organized by service category
 */
export const PORTS = Object.fromEntries(Object.entries(allServices).map(([key, value]) => [key, value.port])) as {
  [K in keyof typeof allServices]: (typeof allServices)[K]['port'];
};

/**
 * Type for port keys
 */
export type PortKey = keyof typeof PORTS;

/**
 * Enum for service names - provides compile-time safety for getPort()/getUrl() calls.
 * Values match the keys in ports.json exactly.
 *
 * @example
 * ```typescript
 * import { getPort, ServiceName } from '@ops-matrix/shared-types';
 * const port = getPort(ServiceName.ACCOUNT_SERVICE);
 * ```
 */
export enum ServiceName {
  // HTTP services
  API_GATEWAY = 'API_GATEWAY',
  INTEGRATION_SERVICE = 'INTEGRATION_SERVICE',
  DOCUMENT_PIPELINE_REST = 'DOCUMENT_PIPELINE_REST',
  NOTIFICATION_SERVICE_WEBHOOKS = 'NOTIFICATION_SERVICE_WEBHOOKS',
  MCP_FRAMEWORK_HTTP = 'MCP_FRAMEWORK_HTTP',

  // Portals
  ADMIN_PORTAL = 'ADMIN_PORTAL',
  BUSINESS_PORTAL = 'BUSINESS_PORTAL',
  CONSUMER_PORTAL = 'CONSUMER_PORTAL',
  AUTH_PORTAL = 'AUTH_PORTAL',

  // AI/ML gRPC services
  MCP_FRAMEWORK = 'MCP_FRAMEWORK',
  AGENT_SERVICE = 'AGENT_SERVICE',
  RETRIEVAL_SERVICE = 'RETRIEVAL_SERVICE',
  MODEL_GATEWAY_SERVICE = 'MODEL_GATEWAY_SERVICE',
  CONTENT_SAFETY_SERVICE = 'CONTENT_SAFETY_SERVICE',
  FEATURE_STORE_SERVICE = 'FEATURE_STORE_SERVICE',
  EVAL_FRAMEWORK_SERVICE = 'EVAL_FRAMEWORK_SERVICE',
  PROMPT_REGISTRY_SERVICE = 'PROMPT_REGISTRY_SERVICE',
  REPORT_GENERATION_SERVICE = 'REPORT_GENERATION_SERVICE',
  COMPLIANCE_SERVICE = 'COMPLIANCE_SERVICE',
  DOCUMENT_PIPELINE = 'DOCUMENT_PIPELINE',
  WORKFLOW_ORCHESTRATION_SERVICE = 'WORKFLOW_ORCHESTRATION_SERVICE',
  DIGITAL_TWIN_SERVICE = 'DIGITAL_TWIN_SERVICE',
  CONTROL_TOWER_SERVICE = 'CONTROL_TOWER_SERVICE',
  CONTEXT_MESSAGE_SERVICE = 'CONTEXT_MESSAGE_SERVICE',

  // Business gRPC services
  ACCOUNT_SERVICE = 'ACCOUNT_SERVICE',
  TENANT_SERVICE = 'TENANT_SERVICE',
  BILLING_SERVICE = 'BILLING_SERVICE',
  NOTIFICATION_SERVICE = 'NOTIFICATION_SERVICE',
  ENTITY_FRAMEWORK_SERVICE = 'ENTITY_FRAMEWORK_SERVICE',
  AUDIT_SERVICE = 'AUDIT_SERVICE',
  CASE_MANAGEMENT_SERVICE = 'CASE_MANAGEMENT_SERVICE',
  SANDBOX_SERVICE = 'SANDBOX_SERVICE',
  SECURITY_SERVICE = 'SECURITY_SERVICE',
  PRICING_ENGINE_SERVICE = 'PRICING_ENGINE_SERVICE',
  CRM_SERVICE = 'CRM_SERVICE',
  DOCUMENT_PIPELINE_STORAGE = 'DOCUMENT_PIPELINE_STORAGE',
  REPORT_GRPC_SERVICE = 'REPORT_GRPC_SERVICE',
  COMPLIANCE_GRPC_SERVICE = 'COMPLIANCE_GRPC_SERVICE',
  PROVISIONING_SERVICE = 'PROVISIONING_SERVICE',
  INTEGRATION_SERVICE_GRPC = 'INTEGRATION_SERVICE_GRPC',

  // Metrics
  MODEL_GATEWAY_METRICS = 'MODEL_GATEWAY_METRICS',
  RETRIEVAL_SERVICE_METRICS = 'RETRIEVAL_SERVICE_METRICS',
  AGENT_SERVICE_METRICS = 'AGENT_SERVICE_METRICS',
  CONTROL_TOWER_METRICS = 'CONTROL_TOWER_METRICS',
}

/**
 * Get port from environment variable, falling back to default
 *
 * @param service - Service name enum (e.g., ServiceName.API_GATEWAY)
 * @returns Port number
 *
 * @example
 * ```typescript
 * import { getPort, ServiceName } from '@ops-matrix/shared-types';
 * const port = getPort(ServiceName.API_GATEWAY);
 * ```
 */
export function getPort(service: PortKey): number {
  const envVar = `${service}_PORT`;
  const envValue = typeof process !== 'undefined' ? process.env[envVar] : undefined;
  if (envValue) {
    const parsed = parseInt(envValue, 10);
    if (!Number.isNaN(parsed)) return parsed;
  }
  return PORTS[service];
}

/**
 * Get URL from environment variable, falling back to default
 *
 * @param service - Service name (e.g., 'API_GATEWAY')
 * @param options - URL options
 * @returns URL string
 *
 * @example
 * ```typescript
 * // Reads API_GATEWAY_URL from env, falls back to ports.json default
 * const url = getUrl('API_GATEWAY');
 *
 * // For gRPC services (no http prefix)
 * const grpcUrl = getUrl('AGENT_SERVICE', { grpc: true });
 * ```
 */
export function getUrl(service: PortKey, options?: { grpc?: boolean }): string {
  const envVar = `${service}_URL`;
  const envValue = typeof process !== 'undefined' ? process.env[envVar] : undefined;
  if (envValue) return envValue;

  const port = getPort(service);
  const host = portsJson.defaults.host;

  // gRPC services use host:port format (no protocol)
  if (options?.grpc) {
    return `${host}:${port}`;
  }

  return `${portsJson.defaults.httpProtocol}://${host}:${port}`;
}

// Determine which services are HTTP vs gRPC
const httpServices = new Set([...Object.keys(portsJson.services.http), ...Object.keys(portsJson.services.portals)]);

/**
 * Pre-built localhost URLs from ports.json (STATIC -- computed at import time).
 *
 * WARNING: These do NOT respect slot-based port overrides (WORKTREE_SLOT).
 * Use getUrl() for runtime URL resolution, which reads env vars dynamically.
 * URLS is safe for Zod schema defaults and documentation only.
 */
export const URLS = Object.fromEntries(
  Object.entries(PORTS).map(([key, port]) => {
    const isHttp = httpServices.has(key);
    const host = portsJson.defaults.host;
    const url = isHttp ? `${portsJson.defaults.httpProtocol}://${host}:${port}` : `${host}:${port}`;
    return [key, url];
  }),
) as { [K in PortKey]: string };

/**
 * Type for URL keys
 */
export type UrlKey = keyof typeof URLS;
